'use client';

import { Connection } from '@solana/web3.js';
import { useQuery } from '@tanstack/react-query';
import { JUPITER_PERPS_PROGRAM_ID } from 'src/constants/jupiter';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';

// Helper function to read a 64-bit integer in browser environments
function readInt64LE(buffer: Buffer, offset: number) {
	// Read the low 32 bits and high 32 bits separately
	const lowBits = buffer.readUInt32LE(offset);
	const highBits = buffer.readInt32LE(offset + 4); // Using signed for high bits

	// Combine them into a JavaScript number
	// Note: This has precision limitations for very large values
	const value = highBits * Math.pow(2, 32) + lowBits;

	return value;
}

// TODO: at time of implementation, Jupiter's API for such data is not available yet - https://station.jup.ag/docs/perp-api/
const fetchJlpPoolAccountData = async (connection: Connection) => {
	if (!connection) return null;

	const response = await fetch(connection.rpcEndpoint, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			jsonrpc: '2.0',
			id: 1,
			method: 'getAccountInfo',
			params: [
				JUPITER_PERPS_PROGRAM_ID,
				{
					encoding: 'base64',
				},
			],
		}),
	});

	const json = await response.json();
	const data = Buffer.from(json.result.value.data[0], 'base64');

	// Start parsing
	let offset = 8; // Skip 8-byte discriminator

	// Parse name length and skip the string
	const nameLength = data.readUInt32LE(offset);
	offset += 4 + nameLength;

	// Parse custodies vector length and skip it
	const custodiesLength = data.readUInt32LE(offset);
	offset += 4 + custodiesLength * 32; // Each PublicKey is 32 bytes

	// Skip fixed-size fields to reach poolApr.lastUpdated
	offset += 16; // aumUsd (u128)
	offset += 16 + 16 + 8; // limit struct
	offset += 9 * 8; // fees struct

	// Now at poolApr.lastUpdated - read the value
	const lastUpdated = readInt64LE(data, offset);
	const feeAprBps = readInt64LE(data, offset + 8);

	return {
		lastUpdatedTs: Number(lastUpdated),
		feeAprBps: Number(feeAprBps),
	};
};

export const useJlpJupPerpsPoolAccountData = () => {
	const connection = useDriftStore((s) => s.connection.current);

	const { data: jlpPoolAccountData } = useQuery({
		queryKey: ['jlpPoolAccountData', connection?.rpcEndpoint],
		queryFn: () => fetchJlpPoolAccountData(connection),
		enabled: !!connection,
		staleTime: 60 * 1000, // Consider data fresh for 1 minute
		refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
	});

	return jlpPoolAccountData;
};
